import { useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getUser, updateUser } from "services/User.services";
import { toast } from "sonner";
import { z } from "zod";

import type { UpdateUserRequest, User } from "types/User.type";

import { queryClient } from "utils/queryClient";

import { buildAboutMeSchema } from "./AboutMe.schema";

export default function useAboutMe() {

  const { t } = useTranslation();

  const { data: user, isLoading: isUserLoading } = useQuery<User>({
    queryKey: ["user"],
    queryFn: () => getUser(),
  });

  const updateUserMutation = useMutation<User, Error, UpdateUserRequest>({
    mutationFn: (user) => updateUser(user),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
      toast.success(t("profile.aboutMe.updateSuccess"), {
        description: t("profile.aboutMe.updateSuccessDescription"),
      });
    },
  });

  const aboutMeSchema = buildAboutMeSchema(t);

  const form = useForm<z.infer<typeof aboutMeSchema>>({
    resolver: zodResolver(aboutMeSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      residency: "",
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = form;

  function onSubmit(values: z.infer<typeof aboutMeSchema>) {
    updateUserMutation.mutate(values);
  }

  useEffect(() => {
    if (user) {
      reset(user);
    }
  }, [user, reset]);

  return { user, isUserLoading, form, isDirty, onSubmit, updateUserMutation, control, handleSubmit };
}
